import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiActivity, 
  FiFilter, 
  FiSearch, 
  FiChevronLeft, 
  FiChevronRight,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiClipboard,
  FiUser,
  FiUsers,
  FiFileText,
  FiHelpCircle,
  FiLoader,
  FiRefreshCw,
  FiCalendar,
  FiX,
  FiArrowLeft,
  FiChevronDown,
  FiChevronUp,
  FiShare2,
  FiGitMerge,
  FiLayers,
  FiHome,
  FiBriefcase,
  FiCornerDownRight,
  FiExternalLink
} from 'react-icons/fi';
import { dashboardApi, crosswalkApi, questionnairesApi } from '../services/api';
import { SHARE_META_MARKER, stripShareMeta, getActionBadgeColor } from '../utils/activityDetails';
import CrosswalkDetailsModal from './admin/Assessments/CrosswalkDetailsModal';

// An "Assessment Shared" activity records the recipients, the crosswalk jobs the share kicked
// off, and (when a source profile was picked) the profile that scoped the crosswalk, in the shape
// written by AssessmentService.LogShareActivityAsync:
//   "Shared with ZenCo (2 user(s)): Jane Doe (reviewer), … . Crosswalk jobs (2): Q1 — queued (Job ID: abc); … . Source profile: X (3 source assessment(s), 2 document(s)) ##share-meta##{…}"
// Everything before the marker is prose; the JSON after it carries the profile's source
// assessments and documents as real lists. Rows logged before the marker existed still parse —
// the profile then falls back to just its name, scraped from the prose.
const parseShareDetails = (details) => {
  const empty = { shared: '', org: null, recipientGroups: [], jobs: [], sourceProfiles: [] };
  if (!details) return empty;

  // 1) Split the structured payload off the end.
  let rest = details;
  let meta = null;
  const metaAt = rest.indexOf(SHARE_META_MARKER);
  if (metaAt !== -1) {
    try {
      meta = JSON.parse(rest.slice(metaAt + SHARE_META_MARKER.length));
    } catch {
      meta = null;
    }
    rest = rest.slice(0, metaAt);
  }

  // 2) Peel off the trailing "Source profile(s) …" sentence — it follows the crosswalk jobs list,
  //    so leaving it in place would get swallowed into the last job's text.
  let profileNamesFromText = [];
  const profileMatch = /\.\s*Source profiles?\s*(?:\(\d+\))?:\s*(.+?)\s*$/.exec(rest);
  if (profileMatch) {
    profileNamesFromText = profileMatch[1]
      // Drop the trailing "— 13 source assessment(s) and 6 document(s)" tally.
      .replace(/\s+—\s+\d+ source assessment\(s\).*$/, '')
      .replace(/\s*\(\d+ source assessment\(s\).*$/, '')
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean);
    rest = rest.slice(0, profileMatch.index);
  }

  // Structured profiles when present; otherwise names scraped from the prose (older rows).
  const sourceProfiles = Array.isArray(meta?.sourceProfiles) && meta.sourceProfiles.length > 0
    ? meta.sourceProfiles.map((p) => ({
        name: p.name || 'Source profile',
        scopeId: p.scopeId || null,
        usedForCrosswalk: !!p.usedForCrosswalk,
        sourceAssessments: p.sourceAssessments || [],
        documents: (p.documents || []).map((d) =>
          typeof d === 'string' ? { key: d, title: d } : { key: d.key, title: d.title || d.key }),
      }))
    // Pre-multi-profile rows carried a single `sourceProfile` object.
    : meta?.sourceProfile
      ? [{
          name: meta.sourceProfile.name || 'Source profile',
          scopeId: meta.sourceProfile.scopeId || null,
          usedForCrosswalk: true,
          sourceAssessments: meta.sourceProfile.sourceAssessments || [],
          documents: (meta.sourceProfile.documents || []).map((d) =>
            typeof d === 'string' ? { key: d, title: d } : { key: d.key, title: d.title || d.key }),
        }]
      : profileNamesFromText.map((n) => ({
          name: n, scopeId: null, usedForCrosswalk: false, sourceAssessments: [], documents: [],
        }));

  // 3) Split the recipients sentence from the crosswalk jobs list.
  const marker = rest.indexOf('Crosswalk jobs (');
  const shared = (marker === -1 ? rest : rest.slice(0, marker)).trim().replace(/\.$/, '');

  // "Shared with ZenCo (2 user(s)): Jane Doe (reviewer), John Smith"
  const sharedMatch = /^Shared with (.+?)(?:\s*\(\d+ user\(s\)\):\s*(.*))?$/.exec(shared);
  const org = sharedMatch ? sharedMatch[1].trim() : null;

  // Recipients grouped by the organization each belongs to, so a share spanning several
  // organizations (or a vendor's users) reads as one row per organization. Older rows have no
  // per-user organization, so they collapse into a single group under the share's target org.
  let recipientGroups = [];
  if (Array.isArray(meta?.recipients) && meta.recipients.length > 0) {
    const byOrg = new Map();
    meta.recipients.forEach((r) => {
      const orgName = r.org || org || 'Unknown organization';
      const key = `${r.ownerType || 'customeraccount'}::${orgName}`;
      if (!byOrg.has(key)) {
        byOrg.set(key, {
          org: orgName,
          isVendor: (r.ownerType || '').toLowerCase() === 'vendor',
          parentOrgs: r.parentOrgs || [],
          users: [],
        });
      }
      byOrg.get(key).users.push({ name: r.name, role: r.role, email: r.email });
    });
    // Organizations first, then vendors, each alphabetically — so the rows don't reshuffle
    // with the order users happened to be picked in.
    recipientGroups = [...byOrg.values()].sort((a, b) =>
      (a.isVendor === b.isVendor) ? a.org.localeCompare(b.org) : (a.isVendor ? 1 : -1));
  } else if (sharedMatch?.[2]) {
    recipientGroups = [{
      org: org || 'Unknown organization',
      isVendor: false,
      parentOrgs: [],
      users: sharedMatch[2].split(',').map((r) => {
        const m = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(r.trim());
        return m ? { name: m[1], role: m[2], email: null } : { name: r.trim(), role: null, email: null };
      }).filter((u) => u.name),
    }];
  }

  let jobs = [];
  if (marker !== -1) {
    const listStart = rest.indexOf('): ', marker);
    if (listStart !== -1) {
      jobs = rest.slice(listStart + 3).split(';').map((entry) => {
        const line = entry.trim().replace(/\.$/, '');
        const match = /^(.*?)\s+—\s+(.*?)\s+\(Job ID:\s*(.*?)\)$/.exec(line);
        return match
          ? { name: match[1], status: match[2], jobId: match[3] }
          : { name: line, status: null, jobId: null };
      }).filter((j) => j.name);
    }
  }

  return { shared, org, recipientGroups, jobs, sourceProfiles };
};

// Colour the crosswalk job status the same way the Crosswalk column does elsewhere.
const jobStatusClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (s === 'failed' || s === 'save_failed' || s === 'timeout') return 'bg-red-100 text-red-700';
  if (s === 'no_proposals') return 'bg-slate-100 text-slate-600';
  return 'bg-blue-100 text-blue-700'; // queued / running / polling / enriching / saving
};

const isShareActivity = (activity) =>
  (activity?.entityType || '').toLowerCase() === 'assessment' &&
  (activity?.action || '').toLowerCase().includes('shared');

// A crosswalk job that is still in flight upstream. Matches the Crosswalk Details modal so the
// two views agree on when to keep polling.
const LIVE_ACTIVE_STATUSES = new Set(['queued', 'running', 'processing', 'polling', 'enriching', 'saving']);
const LIVE_POLL_MS = 10000;

// Proposal count reported by a live job envelope, whichever shape it comes back in.
const liveProposalCount = (job) =>
  job?.result?.proposals?.count
  ?? job?.result?.proposals?.summary?.totalProposals
  ?? job?.result?.persistence?.proposedCount
  ?? 0;

const Activity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filters
  const [entityType, setEntityType] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Column filters (advanced: two conditions per column)
  const defaultFilterState = () => ({ operator1: 'contains', value1: '', logic: 'and', operator2: 'equals', value2: '' });
  const [columnFilters, setColumnFilters] = useState({
    action: defaultFilterState(),
    entityType: defaultFilterState(),
    entityName: defaultFilterState(),
    details: defaultFilterState(),
    userEmail: defaultFilterState(),
    userRole: defaultFilterState(),
  });
  const [appliedFilters, setAppliedFilters] = useState({
    action: defaultFilterState(),
    entityType: defaultFilterState(),
    entityName: defaultFilterState(),
    details: defaultFilterState(),
    userEmail: defaultFilterState(),
    userRole: defaultFilterState(),
  });
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
  const filterRef = useRef(null);

  // Row expansion (full details) and the live Crosswalk Details modal launched from a share row.
  const [expandedId, setExpandedId] = useState(null);
  const [crosswalkAssessment, setCrosswalkAssessment] = useState(null);

  // Per-document state while exchanging an evidence_document_key for a signed URL:
  // { [key]: 'loading' | 'error' }.
  const [docState, setDocState] = useState({});

  // Live crosswalk state for the expanded share row, resolved exactly the way the Crosswalk
  // Details modal resolves it. The activity log records the status at share time — almost always
  // "running", since jobs finish minutes later — so it has to be re-derived to be useful.
  // [{ questionnaireId, name, jobId, proposalCount, status }]
  const [liveRows, setLiveRows] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveRefreshTick, setLiveRefreshTick] = useState(0);

  // Source profiles store only an evidence_document_key, so the blob URL has to be resolved and
  // signed before the file can be opened. The tab is opened synchronously on the click and only
  // then pointed at the URL — opening it after the await would trip the popup blocker.
  const openEvidenceDocument = async (key) => {
    if (!key) return;
    const win = window.open('', '_blank');
    if (win) win.opener = null;
    setDocState((prev) => ({ ...prev, [key]: 'loading' }));
    try {
      const { url } = await crosswalkApi.getEvidenceDocumentUrlByKey(key);
      if (!url) throw new Error('No URL returned for evidence document');
      if (win) win.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer'); // popup was blocked; try directly
      setDocState((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      console.error('Failed to open evidence document', key, err);
      if (win) win.close();
      setDocState((prev) => ({ ...prev, [key]: 'error' }));
    }
  };

  const entityTypes = [
    { value: '', label: 'All Types' },
    { value: 'User', label: 'Users' },
    { value: 'Organization', label: 'Organizations' },
    { value: 'Assessment', label: 'Assessments' },
    { value: 'Questionnaire', label: 'Questionnaires' },
    { value: 'Question', label: 'Questions' },
  ];

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardApi.getActivities(
        page, 
        pageSize, 
        entityType || null, 
        actionFilter || null, 
        userFilter || null, 
        fromDate || null, 
        toDate || null
      );
      setActivities(result.data || []);
      setTotalCount(result.totalCount || 0);
      setTotalPages(result.totalPages || 0);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch activities:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, entityType, actionFilter, userFilter, fromDate, toDate]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleSearch = () => {
    setPage(1);
    fetchActivities();
  };

  const clearFilters = () => {
    setEntityType('');
    setActionFilter('');
    setUserFilter('');
    setFromDate('');
    setToDate('');
    setPage(1);
    // Also clear column filters
    const emptyFilters = {
      action: defaultFilterState(),
      entityType: defaultFilterState(),
      entityName: defaultFilterState(),
      details: defaultFilterState(),
      userEmail: defaultFilterState(),
      userRole: defaultFilterState(),
    };
    setColumnFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  // Column filter evaluation functions
  const evalCondition = (cellVal, operator, filterVal) => {
    if (!filterVal) return null;
    const cell = String(cellVal ?? '').toLowerCase();
    const fv = filterVal.toLowerCase();
    switch (operator) {
      case 'contains': return cell.includes(fv);
      case 'equals': return cell === fv;
      case 'starts-with': return cell.startsWith(fv);
      case 'ends-with': return cell.endsWith(fv);
      case 'not-equals': return cell !== fv;
      case 'not-contains': return !cell.includes(fv);
      default: return null;
    }
  };

  const evalColumnFilter = (cellVal, filter) => {
    const r1 = evalCondition(cellVal, filter.operator1, filter.value1);
    const r2 = evalCondition(cellVal, filter.operator2, filter.value2);
    if (r1 === null && r2 === null) return true;
    if (r1 === null) return r2;
    if (r2 === null) return r1;
    return filter.logic === 'and' ? r1 && r2 : r1 || r2;
  };

  // Apply column filters to activities
  const filteredActivities = useMemo(() => activities.filter(activity => {
    return evalColumnFilter(activity.action, appliedFilters.action)
      && evalColumnFilter(activity.entityType, appliedFilters.entityType)
      && evalColumnFilter(activity.entityName, appliedFilters.entityName)
      && evalColumnFilter(activity.details, appliedFilters.details)
      && evalColumnFilter(activity.userEmail, appliedFilters.userEmail)
      && evalColumnFilter(activity.userRole, appliedFilters.userRole);
    // evalColumnFilter is a pure helper over its arguments; only the data and the applied
    // filters actually change the result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [activities, appliedFilters]);

  // The assessment behind the currently expanded share row.
  const expandedAssessmentId = useMemo(() => {
    if (expandedId === null || expandedId === undefined) return null;
    const activity = filteredActivities.find((a, i) => (a.id || i) === expandedId);
    if (!activity || !isShareActivity(activity)) return null;
    return activity.entityId || null;
  }, [filteredActivities, expandedId]);

  // Resolve the live crosswalk state for that assessment, mirroring CrosswalkDetailsModal:
  //  1. list the assessment's questionnaires;
  //  2. read each one's PERSISTED proposals — this is the authoritative count. Proposals are only
  //     written on the completion path, so any proposal means the job completed even when the
  //     upstream job cache has expired and reports "failed";
  //  3. poll the upstream job batch to refine, and keep polling while anything is genuinely in
  //     flight. The refine step never LOWERS a count — an expired upstream job reports 0, which
  //     would otherwise flip a completed row back to failed (exactly the bug this replaces).
  useEffect(() => {
    if (!expandedAssessmentId) {
      setLiveRows([]);
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const pollBatch = async (rows) => {
      const jobIds = rows.map(r => r.jobId).filter(Boolean);
      if (jobIds.length === 0) return;
      try {
        const batch = await crosswalkApi.getJobStatusBatch(jobIds);
        if (cancelled) return;

        const byId = {};
        (batch || []).forEach(j => { if (j?.jobId) byId[j.jobId] = j; });

        setLiveRows(prev => prev.map(row => {
          const live = row.jobId ? byId[row.jobId] : null;
          if (!live) return row;
          const liveCount = liveProposalCount(live);
          const proposalCount = liveCount > 0 ? liveCount : row.proposalCount;
          return {
            ...row,
            proposalCount,
            status: proposalCount > 0 ? 'completed' : (live.status || row.status),
          };
        }));

        const anyActive = (batch || []).some(j =>
          j && LIVE_ACTIVE_STATUSES.has((j.status || '').toLowerCase()) && liveProposalCount(j) === 0);
        if (anyActive && !cancelled) {
          timer = setTimeout(() => pollBatch(rows), LIVE_POLL_MS);
        }
      } catch {
        /* transient — the persisted counts above still stand */
      }
    };

    const run = async () => {
      setLiveLoading(true);
      try {
        const res = await questionnairesApi.getAll(1, 100, null, null, expandedAssessmentId);
        const items = res?.items || res?.data || (Array.isArray(res) ? res : []);
        if (cancelled) return;

        const rows = await Promise.all(items.map(async (q) => {
          const questionnaireId = q.questionnaireId || q.id;
          const proposalsRes = await crosswalkApi
            .getProposals(expandedAssessmentId, questionnaireId)
            .catch(() => null);
          const proposals = proposalsRes?.proposals || [];
          return {
            questionnaireId,
            name: q.questionnaireName || q.name || '',
            jobId: proposalsRes?.jobId || null,
            proposalCount: proposals.length,
            status: proposals.length > 0
              ? 'completed'
              : (proposalsRes?.status || 'no_proposals'),
          };
        }));
        if (cancelled) return;

        setLiveRows(rows);
        pollBatch(rows);
      } catch (err) {
        console.error('[Activity] Failed to resolve live crosswalk state', err);
      } finally {
        if (!cancelled) setLiveLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [expandedAssessmentId, liveRefreshTick]);

  // Look up a recorded job row's live counterpart. Job id is the precise match; questionnaire
  // name is the fallback, because a later re-share replaces the job id on the questionnaire and
  // the audit row still holds the older one.
  const liveByJobId = useMemo(() => {
    const map = {};
    liveRows.forEach(r => { if (r.jobId) map[r.jobId] = r; });
    return map;
  }, [liveRows]);

  const liveByName = useMemo(() => {
    const map = {};
    liveRows.forEach(r => { if (r.name) map[r.name.trim().toLowerCase()] = r; });
    return map;
  }, [liveRows]);

  // Close filter popover on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setActiveFilter(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterClick = (e, columnKey) => {
    e.stopPropagation();
    if (activeFilter === columnKey) { setActiveFilter(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const popoverWidth = 260;
    let left = rect.left;
    if (left + popoverWidth > window.innerWidth) left = window.innerWidth - popoverWidth - 8;
    setFilterPosition({ top: rect.bottom + 4, left });
    setColumnFilters(f => ({ ...f, [columnKey]: { ...appliedFilters[columnKey] } }));
    setActiveFilter(columnKey);
  };

  const handleApplyFilter = () => {
    setAppliedFilters(f => ({ ...f, [activeFilter]: { ...columnFilters[activeFilter] } }));
    setActiveFilter(null);
  };

  const handleClearColumnFilter = () => {
    const empty = defaultFilterState();
    setColumnFilters(f => ({ ...f, [activeFilter]: empty }));
    setAppliedFilters(f => ({ ...f, [activeFilter]: empty }));
    setActiveFilter(null);
  };

  const hasActiveFilter = (key) => appliedFilters[key].value1 !== '' || appliedFilters[key].value2 !== '';

  const getEntityIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'user': return <FiUser className="text-blue-500" />;
      case 'organization': return <FiUsers className="text-purple-500" />;
      case 'assessment': return <FiClipboard className="text-emerald-500" />;
      case 'questionnaire': return <FiFileText className="text-orange-500" />;
      case 'question': return <FiHelpCircle className="text-amber-500" />;
      default: return <FiActivity className="text-slate-500" />;
    }
  };

  const getStatusIcon = (action) => {
    const actionLower = (action || '').toLowerCase();
    if (actionLower.includes('shared')) {
      return <FiShare2 className="text-indigo-500" />;
    }
    if (actionLower.includes('created') || actionLower.includes('completed')) {
      return <FiCheckCircle className="text-emerald-500" />;
    }
    if (actionLower.includes('pending') || actionLower.includes('review')) {
      return <FiClock className="text-amber-500" />;
    }
    if (actionLower.includes('deleted') || actionLower.includes('alert') || actionLower.includes('failed')) {
      return <FiAlertCircle className="text-red-500" />;
    }
    return <FiActivity className="text-blue-500" />;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiLoader className="w-8 h-8 animate-spin text-[#0077b5]" />
        <span className="ml-2 text-slate-500">Loading activities...</span>
      </div>
    );
  }

  if (error && activities.length === 0) {
    return (
      <div className="rounded-xl border p-4 bg-red-50 border-red-200 text-red-600">
        Error loading activities: {error}
        <button onClick={fetchActivities} className="ml-4 underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0077b5] transition-colors"
      >
        <FiArrowLeft className="w-4 h-4" />
        Back
      </button>

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FiActivity className="text-[#0077b5]" />
            Activity Log
          </h1>
          <p className="mt-1 text-slate-500">
            Real-time tracking of all user activities across the platform
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={fetchActivities}
            className="px-4 py-2 border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border text-sm font-medium flex items-center gap-2 ${
              showFilters ? 'bg-[#0077b5] text-white border-[#0077b5]' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <FiFilter /> Filters
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Filter Activities</h3>
            <button onClick={clearFilters} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
              <FiX size={14} /> Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Entity Type</label>
              <select 
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
              >
                {entityTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Action</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  placeholder="e.g., Created, Updated"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">User</label>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  placeholder="Filter by user"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">From Date</label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">To Date</label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button 
              onClick={handleSearch}
              className="px-4 py-2 bg-[#0077b5] text-white rounded-lg text-sm font-medium hover:bg-[#006699]"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 cardStyle">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FiActivity className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{totalCount}</p>
              <p className="text-sm text-slate-500">Total Activities</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 cardStyle">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <FiCheckCircle className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {activities.filter(a => (a.action || '').toLowerCase().includes('created')).length}
              </p>
              <p className="text-sm text-slate-500">Created (page)</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 cardStyle">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <FiClock className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {activities.filter(a => (a.action || '').toLowerCase().includes('updated')).length}
              </p>
              <p className="text-sm text-slate-500">Updated (page)</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 cardStyle">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <FiAlertCircle className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {activities.filter(a => (a.action || '').toLowerCase().includes('deleted')).length}
              </p>
              <p className="text-sm text-slate-500">Deleted (page)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activities Table */}
      <div className="bg-white p-4 cardStyle overflow-hidden">
        <div className="w-full max-w-full overflow-x-auto">
          <table className="w-full table-fixed">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="hidden xl:table-cell w-14 px-2 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                <th className="w-28 px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center justify-between">
                    Action
                    <button onClick={(e) => handleFilterClick(e, 'action')} className={`p-0.5 rounded ${hasActiveFilter('action') ? 'text-[#0077b5]' : 'text-slate-400 hover:text-slate-600'}`}>
                      <FiFilter className="w-3 h-3" />
                    </button>
                  </div>
                </th>
                <th className="w-40 px-3 xl:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center justify-between">
                    Entity
                    <button onClick={(e) => handleFilterClick(e, 'entityType')} className={`p-0.5 rounded ${hasActiveFilter('entityType') ? 'text-[#0077b5]' : 'text-slate-400 hover:text-slate-600'}`}>
                      <FiFilter className="w-3 h-3" />
                    </button>
                  </div>
                </th>
                <th className="hidden lg:table-cell w-36 xl:w-44 px-3 xl:px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center justify-between">
                    Details
                    <button onClick={(e) => handleFilterClick(e, 'details')} className={`p-0.5 rounded ${hasActiveFilter('details') ? 'text-[#0077b5]' : 'text-slate-400 hover:text-slate-600'}`}>
                      <FiFilter className="w-3 h-3" />
                    </button>
                  </div>
                </th>
                <th className="w-44 xl:w-56 px-3 xl:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center justify-between">
                    User Email
                    <button onClick={(e) => handleFilterClick(e, 'userEmail')} className={`p-0.5 rounded ${hasActiveFilter('userEmail') ? 'text-[#0077b5]' : 'text-slate-400 hover:text-slate-600'}`}>
                      <FiFilter className="w-3 h-3" />
                    </button>
                  </div>
                </th>
                <th className="hidden lg:table-cell w-32 px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center justify-between">
                    Role
                    <button onClick={(e) => handleFilterClick(e, 'userRole')} className={`p-0.5 rounded ${hasActiveFilter('userRole') ? 'text-[#0077b5]' : 'text-slate-400 hover:text-slate-600'}`}>
                      <FiFilter className="w-3 h-3" />
                    </button>
                  </div>
                </th>
                <th className="hidden md:table-cell w-32 px-3 xl:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Timestamp</th>
                <th className="w-10 px-2 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredActivities.map((activity, index) => {
                const rowKey = activity.id || index;
                const isExpanded = expandedId === rowKey;
                const share = isShareActivity(activity) ? parseShareDetails(activity.details) : null;
                return (
                <React.Fragment key={rowKey}>
                <tr
                  className="hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : rowKey)}
                >
                  <td className="hidden xl:table-cell px-2 py-4">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      {getStatusIcon(activity.action)}
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <span
                      title={activity.action || undefined}
                      className={`block truncate px-2.5 py-1 rounded-full text-xs font-medium text-center ${getActionBadgeColor(activity.action)}`}
                    >
                      {activity.action || '-'}
                    </span>
                  </td>
                  <td className="px-3 xl:px-6 py-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex-shrink-0">{getEntityIcon(activity.entityType)}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate" title={activity.entityType || undefined}>{activity.entityType || '-'}</p>
                        <p className="text-xs text-slate-500 truncate" title={activity.entityName || undefined}>{activity.entityName || '-'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="hidden lg:table-cell w-36 xl:w-44 px-3 xl:px-4 py-4">
                    <p className="text-sm text-slate-600 truncate" title={(share ? (share.shared || stripShareMeta(activity.details)) : activity.details) || undefined}>
                      {share ? (share.shared || stripShareMeta(activity.details)) : (activity.details || '-')}
                    </p>
                  </td>
                  <td className="px-3 xl:px-6 py-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-6 h-6 rounded-full bg-[#0077b5] flex items-center justify-center text-white text-xs flex-shrink-0">
                        {(activity.userEmail || 'U')[0].toUpperCase()}
                      </div>
                      <span className="text-sm text-slate-600 truncate" title={activity.userEmail || undefined}>{activity.userEmail || '-'}</span>
                    </div>
                  </td>
                  <td className="hidden lg:table-cell px-3 py-4">
                    <span className="inline-block whitespace-normal break-words px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      {activity.userRole || '-'}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-3 xl:px-6 py-4">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-600 truncate" title={formatTimestamp(activity.timestamp) || undefined}>{formatTimestamp(activity.timestamp)}</p>
                      <p className="text-xs text-slate-400 truncate">{activity.timeAgo || '-'}</p>
                    </div>
                  </td>
                  <td className="px-2 py-4">
                    {activity.details && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : rowKey); }}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-md border transition-colors ${
                          isExpanded
                            ? 'bg-[#0077b5] border-[#0077b5] text-white'
                            : 'border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                        }`}
                        aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                        title={isExpanded ? 'Collapse details' : 'Expand details'}
                      >
                        {isExpanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                      </button>
                    )}
                  </td>
                </tr>

                {/* Expanded detail — for a share, the recipients, the source profile that scoped
                    the crosswalk (with the assessments and documents it draws from), and the jobs
                    it triggered. Anything else just shows its recorded Details text. */}
                {isExpanded && (
                  <tr className="bg-slate-50/70">
                    <td colSpan={8} className="px-6 pt-2 pb-5">
                      <div className="space-y-4">

                        {/* ── Who received it — one row per organization ── */}
                        {share ? (
                          <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border-b border-indigo-100">
                              <FiUsers className="text-indigo-600 w-3.5 h-3.5" />
                              <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">
                                Shared With ({share.recipientGroups.reduce((n, g) => n + g.users.length, 0)} user(s))
                              </p>
                            </div>
                            {share.recipientGroups.length > 0 ? (
                              <div className="bg-white divide-y divide-slate-100">
                                {share.recipientGroups.map((group, gi) => (
                                  <div key={gi} className="flex flex-col sm:flex-row sm:items-start gap-3 px-4 py-3">
                                    {/* Organization — a vendor also names the account it works through */}
                                    <div className="sm:w-56 flex-shrink-0">
                                      <div className="flex items-center gap-1.5">
                                        {group.isVendor
                                          ? <FiBriefcase className="text-orange-500 w-3.5 h-3.5 flex-shrink-0" />
                                          : <FiHome className="text-indigo-500 w-3.5 h-3.5 flex-shrink-0" />}
                                        <span className="text-sm font-semibold text-slate-800 break-words">{group.org}</span>
                                      </div>
                                      <div className="flex items-center gap-1 mt-0.5 ml-5">
                                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                                          group.isVendor ? 'bg-orange-100 text-orange-700' : 'bg-indigo-100 text-indigo-700'
                                        }`}>
                                          {group.isVendor ? 'Vendor' : 'Organization'}
                                        </span>
                                      </div>
                                      {group.isVendor && group.parentOrgs.length > 0 && (
                                        <p className="flex items-start gap-1 mt-1 ml-5 text-[11px] text-slate-500">
                                          <FiCornerDownRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                          <span className="break-words">via {group.parentOrgs.join(', ')}</span>
                                        </p>
                                      )}
                                    </div>

                                    {/* That organization's recipients */}
                                    <div className="flex flex-wrap gap-1.5 min-w-0">
                                      {group.users.map((u, ui) => (
                                        <span
                                          key={ui}
                                          title={u.email || u.name}
                                          className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full text-xs bg-slate-50 border border-slate-200 text-slate-700"
                                        >
                                          <span className={`w-5 h-5 rounded-full text-white flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${
                                            group.isVendor ? 'bg-orange-500' : 'bg-indigo-500'
                                          }`}>
                                            {u.name?.[0]?.toUpperCase() || 'U'}
                                          </span>
                                          <span className="break-words">{u.name}</span>
                                          {u.role && <span className="text-slate-400">({u.role})</span>}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="px-4 py-3 text-sm text-slate-400 bg-white">No recipients recorded</p>
                            )}
                          </div>
                        ) : (
                          <div className="bg-white border border-slate-200 rounded-lg p-4">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Details</p>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                              {activity.details || 'No further details recorded.'}
                            </p>
                          </div>
                        )}

                        {/* ── The source profiles that scoped the crosswalk, and what each draws from ── */}
                        {share && (
                          share.sourceProfiles.length > 0 ? (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <FiLayers className="text-purple-500 w-4 h-4" />
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                  Source Profiles ({share.sourceProfiles.length})
                                </p>
                              </div>

                              {share.sourceProfiles.map((profile, pi) => (
                                <div key={profile.scopeId || pi} className="border border-slate-200 rounded-lg overflow-hidden">
                                  <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 bg-purple-50 border-b border-purple-100">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <FiLayers className="text-purple-500 w-3.5 h-3.5 flex-shrink-0" />
                                      <span className="text-sm font-semibold text-slate-800 break-words">{profile.name}</span>
                                      {profile.usedForCrosswalk && share.sourceProfiles.length > 1 && (
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-purple-600 text-white flex-shrink-0">
                                          scoped the crosswalk
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                        <FiClipboard className="w-3 h-3" /> {profile.sourceAssessments.length}
                                      </span>
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                        <FiFileText className="w-3 h-3" /> {profile.documents.length}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-100 bg-white">
                                    <div>
                                      <p className="px-4 pt-2.5 pb-1 text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">
                                        Source Assessments ({profile.sourceAssessments.length})
                                      </p>
                                      {profile.sourceAssessments.length > 0 ? (
                                        <ul className="pb-2">
                                          {profile.sourceAssessments.map((a, i) => (
                                            <li key={i} className="px-4 py-1.5 text-sm text-slate-700 flex items-start gap-2">
                                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                                              <span className="break-words">{a}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <p className="px-4 pb-3 text-sm text-slate-400">None</p>
                                      )}
                                    </div>

                                    <div>
                                      <p className="px-4 pt-2.5 pb-1 text-[11px] font-semibold text-amber-700 uppercase tracking-wide">
                                        Documents ({profile.documents.length})
                                      </p>
                                      {profile.documents.length > 0 ? (
                                        <ul className="pb-2">
                                          {profile.documents.map((d, i) => (
                                            <li key={d.key || i} className="px-4 py-1.5">
                                              <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); openEvidenceDocument(d.key); }}
                                                disabled={docState[d.key] === 'loading'}
                                                title={`Open ${d.title} in a new tab (${d.key})`}
                                                className="group flex items-start gap-2 text-left disabled:cursor-wait"
                                              >
                                                {docState[d.key] === 'loading'
                                                  ? <FiLoader className="text-amber-500 w-3.5 h-3.5 mt-0.5 flex-shrink-0 animate-spin" />
                                                  : <FiFileText className="text-amber-400 w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
                                                <span className="text-sm text-[#0b79b8] group-hover:text-[#095a8a] group-hover:underline break-words">
                                                  {d.title}
                                                </span>
                                                <FiExternalLink className="text-slate-300 group-hover:text-[#0b79b8] w-3 h-3 mt-1 flex-shrink-0" />
                                              </button>
                                              {docState[d.key] === 'error' && (
                                                <p className="ml-6 text-[11px] text-red-600">
                                                  Could not open this document.
                                                </p>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <p className="px-4 pb-3 text-sm text-slate-400">
                                          None — this profile carries no evidence documents.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-lg">
                              <FiLayers className="text-slate-300 w-4 h-4 flex-shrink-0" />
                              <p className="text-sm text-slate-400">
                                No source profile — the crosswalk drew from all available sources.
                              </p>
                            </div>
                          )
                        )}

                        {/* ── Crosswalk jobs the share kicked off ── */}
                        {share && share.jobs.length > 0 && (
                          <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className="flex items-center justify-between gap-2 flex-wrap px-4 py-2 bg-slate-100 border-b border-slate-200">
                              <div className="flex items-center gap-2">
                                <FiGitMerge className="text-[#0b79b8] w-3.5 h-3.5" />
                                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                  Crosswalk Jobs ({share.jobs.length})
                                </p>
                                {liveLoading && (
                                  <FiLoader className="w-3 h-3 text-slate-400 animate-spin" />
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setLiveRefreshTick(t => t + 1); }}
                                disabled={liveLoading}
                                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#0b79b8] hover:text-[#095a8a] disabled:opacity-50"
                                title="Re-check the live status of these jobs"
                              >
                                <FiRefreshCw className={`w-3 h-3 ${liveLoading ? 'animate-spin' : ''}`} /> Refresh live status
                              </button>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide bg-white border-b border-slate-100">
                                    <th className="px-4 py-2">Questionnaire</th>
                                    <th className="px-4 py-2">Live Status</th>
                                    <th className="px-4 py-2">At Share</th>
                                    <th className="px-4 py-2">Proposals</th>
                                    <th className="px-4 py-2">Job ID</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                  {share.jobs.map((job, jobIndex) => {
                                    const live = (job.jobId && liveByJobId[job.jobId])
                                      || liveByName[(job.name || '').trim().toLowerCase()]
                                      || null;
                                    const liveStatus = live?.status;
                                    const isActive = LIVE_ACTIVE_STATUSES.has((liveStatus || '').toLowerCase());
                                    // The recorded status is only worth repeating when it differs
                                    // from the live one — otherwise the column is noise.
                                    const showRecorded = job.status && liveStatus && job.status !== liveStatus;
                                    return (
                                      <tr key={job.jobId || jobIndex} className="hover:bg-slate-50/70">
                                        <td className="px-4 py-2.5 font-medium text-slate-800">{job.name}</td>
                                        <td className="px-4 py-2.5">
                                          {liveStatus ? (
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${jobStatusClass(liveStatus)}`}>
                                              {isActive && <FiLoader className="w-3 h-3 animate-spin" />}
                                              {liveStatus}
                                            </span>
                                          ) : liveLoading ? (
                                            <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                                              <FiLoader className="w-3 h-3 animate-spin" /> checking…
                                            </span>
                                          ) : (
                                            <span className="text-xs text-slate-400" title="Upstream could not resolve this job">
                                              unavailable
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-2.5">
                                          {showRecorded ? (
                                            <span className="text-[11px] text-slate-400">{job.status}</span>
                                          ) : (
                                            <span className="text-[11px] text-slate-300">—</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-slate-600">
                                          {live ? live.proposalCount : <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5 font-mono text-[11px] text-slate-400">{job.jobId || '-'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* ── Footer actions ── */}
                        <div className="flex items-center gap-4 flex-wrap pt-0.5">
                          <span className="text-xs text-slate-400">
                            {activity.entityType} #{activity.entityId}
                          </span>
                          {share && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setCrosswalkAssessment({ id: activity.entityId, accountName: activity.entityName });
                              }}
                              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-[#0b79b8] border border-[#0b79b8]/40 rounded-lg hover:bg-[#0b79b8]/5 transition-colors"
                            >
                              <FiGitMerge /> View live crosswalk details
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
              {filteredActivities.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-sm text-slate-500">
                    No activities found. Activities will appear here when users perform actions.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Column filter popover */}
        {activeFilter && (
          <div 
            ref={filterRef} 
            style={{ position: 'fixed', top: filterPosition.top, left: filterPosition.left, zIndex: 99999 }} 
            className="w-64 rounded-lg shadow-lg border bg-white border-slate-200 p-4"
          >
            <p className="text-sm font-semibold text-slate-800 mb-3">Filter</p>

            {/* First condition */}
            <select 
              value={columnFilters[activeFilter]?.operator1} 
              onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], operator1: e.target.value } }))} 
              className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-slate-300 text-slate-700 mb-2"
            >
              <option value="contains">Contains</option>
              <option value="equals">Equals</option>
              <option value="starts-with">Starts with</option>
              <option value="ends-with">Ends with</option>
              <option value="not-equals">Not equals</option>
              <option value="not-contains">Does not contain</option>
            </select>
            <input 
              type="text" 
              autoFocus 
              placeholder="Enter value..." 
              value={columnFilters[activeFilter]?.value1} 
              onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], value1: e.target.value } }))} 
              className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-slate-300 text-slate-700 mb-3" 
            />

            {/* Logic operator */}
            <select 
              value={columnFilters[activeFilter]?.logic} 
              onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], logic: e.target.value } }))} 
              className="w-24 px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-slate-300 text-slate-700 mb-3"
            >
              <option value="and">And</option>
              <option value="or">Or</option>
            </select>

            {/* Second condition */}
            <select 
              value={columnFilters[activeFilter]?.operator2} 
              onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], operator2: e.target.value } }))} 
              className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-slate-300 text-slate-700 mb-2"
            >
              <option value="contains">Contains</option>
              <option value="equals">Equals</option>
              <option value="starts-with">Starts with</option>
              <option value="ends-with">Ends with</option>
              <option value="not-equals">Not equals</option>
              <option value="not-contains">Does not contain</option>
            </select>
            <input 
              type="text" 
              placeholder="Enter value..." 
              value={columnFilters[activeFilter]?.value2} 
              onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], value2: e.target.value } }))} 
              className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-slate-300 text-slate-700 mb-4" 
            />

            {/* Actions */}
            <div className="flex items-center justify-between">
              <button 
                onClick={handleClearColumnFilter} 
                className="px-4 py-1.5 text-sm font-semibold border border-[#F47F2E] text-[#F47F2E] rounded hover:bg-[#F47F2E]/10 transition-colors"
              >
                CLEAR
              </button>
              <button 
                onClick={handleApplyFilter} 
                className="px-4 py-1.5 text-sm font-semibold border border-[#F47F2E] text-[#F47F2E] rounded hover:bg-[#F47F2E]/10 transition-colors"
              >
                APPLY
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
            <div className="text-sm text-slate-500">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} activities
            </div>
            <div className="flex items-center gap-2">
              <select 
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="px-3 py-1.5 border border-slate-300 text-sm"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
              >
                <FiChevronLeft />
              </button>
              <span className="px-3 py-1.5 text-sm text-slate-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Live crosswalk state for the shared assessment (same modal as the Assessments page) */}
      <CrosswalkDetailsModal
        isOpen={!!crosswalkAssessment}
        onClose={() => setCrosswalkAssessment(null)}
        assessment={crosswalkAssessment}
      />
    </div>
  );
};

export default Activity;
